import { useMemo, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { format } from 'date-fns';
import { ClipboardList, Loader2 } from 'lucide-react';
import { leadsApi, LeadAssignmentAuditRow } from '@/api/leads';
import { hospitalAdminApi } from '@/api/hospitalAdmin';
import { useAuth } from '@/contexts/AuthContext';
import { useChainReportHospitalScope } from '@/hooks/useChainReportHospitalScope';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

const PAGE_SIZE = 25;

export default function LeadAssignmentAuditPage() {
  const { user } = useAuth();
  const isSalesPerson = user?.role === 'SALES_PERSON';
  const isManager = user?.role === 'SALES_MANAGER';
  const canFilterStaff = isManager || user?.role === 'HOSPITAL_ADMIN' || user?.role === 'SUPER_ADMIN';

  const {
    showHospitalFilter,
    selectedHospitalId,
    setSelectedHospitalId,
    reportHospitalId,
    hospitalOptions,
  } = useChainReportHospitalScope({ defaultScope: 'consolidated' });

  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [search, setSearch] = useState('');
  const [staffId, setStaffId] = useState<string>('all');
  const [page, setPage] = useState(1);

  const { data: staffOptions = [] } = useQuery({
    queryKey: ['crm-staff-options-audit', user?.hospitalId],
    queryFn: async () => {
      const rows = await hospitalAdminApi.getStaff();
      return (rows || []).filter(
        (s: { role?: string; status?: string }) =>
          (s?.role === 'SALES_PERSON' || s?.role === 'SALES_MANAGER') &&
          (s?.status ?? 'ACTIVE') === 'ACTIVE'
      );
    },
    enabled: !!user?.hospitalId && canFilterStaff,
    staleTime: 60_000,
  });

  const queryParams = useMemo(() => {
    const p: {
      page: number;
      pageSize: number;
      from?: string;
      to?: string;
      search?: string;
      staffId?: string;
      hospitalId?: string;
    } = { page, pageSize: PAGE_SIZE };
    if (from) p.from = from;
    if (to) p.to = to;
    const q = search.trim();
    if (q.length >= 2) p.search = q;
    if (canFilterStaff && staffId !== 'all') p.staffId = staffId;
    if (showHospitalFilter && reportHospitalId) p.hospitalId = reportHospitalId;
    return p;
  }, [page, from, to, search, staffId, canFilterStaff, showHospitalFilter, reportHospitalId]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ['lead-assignment-audit', queryParams],
    queryFn: async () => {
      const res = await leadsApi.listAssignmentAudit(queryParams);
      return res.data?.data ?? { rows: [], total: 0 };
    },
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const scopeHint = isSalesPerson
    ? 'Showing reassignments for leads you own or where you were the previous or new owner.'
    : isManager
      ? 'Showing reassignments for your team and leads you changed.'
      : 'Showing all reassignments in your hospital scope.';

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ClipboardList className="h-5 w-5" />
            Lead assignment log
          </CardTitle>
          <p className="text-sm text-muted-foreground">{scopeHint}</p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex flex-wrap gap-4 items-end">
            {showHospitalFilter && (
              <div>
                <Label>Hospital</Label>
                <Select value={selectedHospitalId || 'all'} onValueChange={setSelectedHospitalId}>
                  <SelectTrigger className="w-52">
                    <SelectValue placeholder="All hospitals" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All hospitals</SelectItem>
                    {hospitalOptions.map((h) => (
                      <SelectItem key={h.id} value={h.id}>
                        {h.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div>
              <Label>From</Label>
              <Input type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} />
            </div>
            <div>
              <Label>To</Label>
              <Input type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} />
            </div>
            {canFilterStaff && (
              <div>
                <Label>Sales person</Label>
                <Select value={staffId} onValueChange={setStaffId}>
                  <SelectTrigger className="w-48">
                    <SelectValue placeholder="All" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All in scope</SelectItem>
                    {staffOptions.map((s: { id: string; name: string }) => (
                      <SelectItem key={s.id} value={s.id}>
                        {s.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
            <div className="min-w-[12rem] flex-1">
              <Label>Search</Label>
              <Input
                placeholder="Patient, phone, or reason…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
            <Button
              variant="outline"
              onClick={() => {
                setFrom('');
                setTo('');
                setSearch('');
                setStaffId('all');
                setPage(1);
              }}
            >
              Clear
            </Button>
          </div>

          {isLoading ? (
            <div className="flex items-center gap-2 text-muted-foreground py-8">
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading…
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>When</TableHead>
                    {showHospitalFilter && <TableHead>Hospital</TableHead>}
                    <TableHead>Patient</TableHead>
                    <TableHead>From</TableHead>
                    <TableHead>To</TableHead>
                    <TableHead>Reason</TableHead>
                    <TableHead>Changed by</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {rows.length === 0 ? (
                    <TableRow>
                      <TableCell
                        colSpan={showHospitalFilter ? 7 : 6}
                        className="text-center text-muted-foreground py-8"
                      >
                        No assignment records
                      </TableCell>
                    </TableRow>
                  ) : (
                    rows.map((r: LeadAssignmentAuditRow) => (
                      <TableRow key={r.id}>
                        <TableCell className="whitespace-nowrap text-sm">
                          {format(new Date(r.changedAt), 'dd MMM yyyy, HH:mm')}
                        </TableCell>
                        {showHospitalFilter && (
                          <TableCell className="text-sm">{r.hospitalName}</TableCell>
                        )}
                        <TableCell className="text-sm">
                          <div className="font-medium">{r.patientName}</div>
                          <div className="text-xs text-muted-foreground">{r.phone}</div>
                        </TableCell>
                        <TableCell className="text-sm">{r.fromStaffName ?? 'Unassigned'}</TableCell>
                        <TableCell className="text-sm">{r.toStaffName ?? 'Unassigned'}</TableCell>
                        <TableCell className="text-sm max-w-xs">{r.reason ?? '—'}</TableCell>
                        <TableCell className="text-sm">{r.changedByName ?? '—'}</TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
              <div className="flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  {total} record{total === 1 ? '' : 's'}
                  {isFetching && !isLoading ? ' · Updating…' : ''}
                </span>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page <= 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                  >
                    Previous
                  </Button>
                  <span>
                    Page {page} of {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={page >= totalPages}
                    onClick={() => setPage((p) => p + 1)}
                  >
                    Next
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
